import { useEffect, useState } from 'react';
import { resolvePhotoPreviewUrl } from './photoPreviewResolver';
import type { CalendarProject } from '../types/calendarProject';

type PreviewUrlState = Record<string, string>;

export function usePhotoPreviewUrls(project: CalendarProject | null) {
  const [previewUrls, setPreviewUrls] = useState<PreviewUrlState>({});

  useEffect(() => {
    if (!project) {
      setPreviewUrls({});
      return undefined;
    }

    const sourceProject = project;
    let isMounted = true;
    const generatedUrls: string[] = [];

    async function loadPreviews() {
      const entries = await Promise.all(sourceProject.photos.map(async (photo) => {
        const preview = await resolvePhotoPreviewUrl(sourceProject, photo.id);
        if (preview.url && preview.shouldRevoke) generatedUrls.push(preview.url);
        return [photo.id, preview.url] as const;
      }));

      if (!isMounted) {
        generatedUrls.forEach((url) => URL.revokeObjectURL(url));
        return;
      }

      setPreviewUrls(Object.fromEntries(entries.filter((entry): entry is [string, string] => Boolean(entry[1]))));
    }

    void loadPreviews();

    return () => {
      isMounted = false;
      generatedUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [project]);

  return previewUrls;
}
